import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { subscriptionAPI, razorpayAPI } from '../../lib/api';
import { 
  CreditCard, 
  Check, 
  AlertTriangle,
  Zap,
  Building2,
  Users,
  FileText,
  Receipt,
  Download,
  Sparkles,
  Shield,
  Loader2
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { apiErrorMessage } from '../../lib/utils';
import { logger } from '../../lib/logger';

export default function SubscriptionPage() {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [history, setHistory] = useState({ subscriptions: [], payments: [] });
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [showCheckout, setShowCheckout] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [razorpayConfig, setRazorpayConfig] = useState(null);
  const [planChangePreview, setPlanChangePreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [plansRes, currentRes, historyRes, configRes] = await Promise.all([
        subscriptionAPI.getPlans(),
        subscriptionAPI.getCurrent(),
        subscriptionAPI.getHistory(),
        razorpayAPI.getConfig()
      ]);
      setPlans(plansRes.data);
      setCurrentSubscription(currentRes.data);
      setHistory(historyRes.data);
      setRazorpayConfig(configRes.data);
    } catch (error) {
      toast.error('Failed to load subscription data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Load Razorpay script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const handleRazorpayPayment = async () => {
    if (!selectedPlan) return;
    
    setSubscribing(true);
    
    try {
      // Create Razorpay order
      const orderRes = await razorpayAPI.createOrder({
        plan_id: selectedPlan.id,
        billing_cycle: billingCycle
      });
      
      const orderData = orderRes.data;
      
      // Configure Razorpay options
      const options = {
        key: orderData.key_id,
        amount: orderData.amount_in_paise,
        currency: orderData.currency,
        name: 'HMC Buddy',
        description: `${orderData.plan_name} - ${billingCycle === 'yearly' ? 'Yearly' : 'Monthly'} Subscription`,
        order_id: orderData.order_id,
        handler: async function (response) {
          // Verify payment on backend
          try {
            const verifyRes = await razorpayAPI.verifyPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              plan_id: selectedPlan.id,
              billing_cycle: billingCycle
            });
            
            toast.success(verifyRes.data.message || 'Subscription activated successfully!');
            setShowCheckout(false);
            fetchData();
          } catch (error) {
            toast.error(apiErrorMessage(error, 'Payment verification failed'));
          }
        },
        prefill: orderData.prefill,
        notes: {
          institution_name: orderData.institution_name
        },
        theme: {
          color: '#0891b2' // cyan-600
        },
        modal: {
          ondismiss: function() {
            setSubscribing(false);
            toast.info('Payment cancelled');
          }
        }
      };
      
      // Open Razorpay checkout
      const razorpay = new window.Razorpay(options);
      razorpay.on('payment.failed', function (response) {
        toast.error(`Payment failed: ${response.error.description}`);
        setSubscribing(false);
      });
      razorpay.open();
      
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to initiate payment'));
      setSubscribing(false);
    }
  };

  const handleMockSubscribe = async () => {
    if (!selectedPlan) return;
    setSubscribing(true);
    try {
      const res = await subscriptionAPI.subscribe({
        plan_id: selectedPlan.id,
        billing_cycle: billingCycle,
        payment_method: 'card'
      });
      toast.success(res.data.message);
      setShowCheckout(false);
      fetchData();
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Subscription failed'));
    } finally {
      setSubscribing(false);
    }
  };

  const handleSubscribe = async () => {
    // Use Razorpay if configured, otherwise mock
    if (razorpayConfig?.is_configured && currentSubscription?.payment_mode === 'razorpay') {
      await handleRazorpayPayment();
    } else {
      await handleMockSubscribe();
    }
  };

  const handleCancel = async () => {
    try {
      const res = await subscriptionAPI.cancel({ reason: cancelReason });
      toast.success(res.data.message);
      setShowCancelModal(false);
      setCancelReason('');
      fetchData();
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to cancel subscription'));
    }
  };

  const handlePlanSelect = async (plan) => {
    setSelectedPlan(plan);
    
    // If already subscribed, preview the plan change
    if (isSubscribed && currentPlanId !== plan.id) {
      setLoadingPreview(true);
      try {
        const res = await subscriptionAPI.previewChange(plan.id, billingCycle);
        setPlanChangePreview(res.data);
      } catch (error) {
        logger.error('Failed to preview plan change:', error);
        setPlanChangePreview(null);
      } finally {
        setLoadingPreview(false);
      }
    } else {
      setPlanChangePreview(null);
    }
    
    setShowCheckout(true);
  };

  const handlePlanChange = async () => {
    if (!selectedPlan) return;
    setSubscribing(true);
    
    try {
      const res = await subscriptionAPI.changePlan({
        new_plan_id: selectedPlan.id,
        billing_cycle: billingCycle
      });
      
      if (res.data.success) {
        toast.success(res.data.message);
        setShowCheckout(false);
        setPlanChangePreview(null);
        fetchData();
      } else if (res.data.requires_payment) {
        // Handle Razorpay payment for upgrade
        toast.info('Redirecting to payment...');
        // Similar to handleRazorpayPayment but for upgrade
      }
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to change plan'));
    } finally {
      setSubscribing(false);
    }
  };

  const getPlanIcon = (planId) => {
    switch (planId) {
      case 'starter': return <Building2 className="w-6 h-6" />;
      case 'professional': return <Zap className="w-6 h-6" />;
      case 'enterprise': return <Sparkles className="w-6 h-6" />;
      default: return <CreditCard className="w-6 h-6" />;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'trial':
        return <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">Trial</span>;
      case 'active':
        return <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium">Active</span>;
      case 'cancelled':
        return <span className="px-2 py-1 bg-slate-100 text-slate-700 rounded-full text-xs font-medium">Cancelled</span>;
      case 'expired':
        return <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">Expired</span>;
      default:
        return null;
    }
  };

  const getPaymentModeBadge = () => {
    if (currentSubscription?.payment_mode === 'razorpay') {
      if (razorpayConfig?.is_configured) {
        return (
          <span className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm font-medium flex items-center gap-1">
            <Shield className="w-4 h-4" />
            Razorpay Live
          </span>
        );
      } else {
        return (
          <span className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-sm font-medium flex items-center gap-1">
            <AlertTriangle className="w-4 h-4" />
            Razorpay Not Configured
          </span>
        );
      }
    }
    return (
      <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">
        Test Mode
      </span>
    );
  };

  if (!user?.institution_id) {
    return (
      <div className="p-8">
        <Card className="max-w-2xl mx-auto">
          <CardContent className="py-12 text-center">
            <AlertTriangle className="w-16 h-16 mx-auto text-amber-400 mb-4" />
            <h2 className="text-xl font-semibold text-slate-700 mb-2">Platform Administrator</h2>
            <p className="text-slate-500">
              Subscription management is only available for institution users.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-slate-200 rounded w-1/4"></div>
          <div className="grid grid-cols-3 gap-6">
            {[1,2,3].map(i => <div key={i} className="h-96 bg-slate-200 rounded"></div>)}
          </div>
        </div>
      </div>
    );
  }

  const isSubscribed = currentSubscription?.subscription?.status === 'active';
  const currentPlanId = currentSubscription?.subscription?.plan_id;

  return (
    <div className="p-8 space-y-8" data-testid="subscription-page">
      {/* Expired Subscription Alert */}
      {(currentSubscription?.subscription_status === 'expired' || 
        currentSubscription?.subscription_status === 'trial_expired' ||
        currentSubscription?.status === 'expired') && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <AlertTriangle className="w-8 h-8 text-red-600 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-bold text-red-800 text-lg">Your Trial Has Expired</h3>
                <p className="text-red-700 mt-1 mb-4">
                  To continue using HMC Buddy and access all features, please choose a subscription plan below.
                </p>
                <div className="bg-white rounded-lg p-4 space-y-2">
                  <p className="text-sm text-slate-700">
                    <strong>What you get with a paid subscription:</strong>
                  </p>
                  <ul className="text-sm text-slate-600 space-y-1 ml-4 list-disc">
                    <li>Unlimited patient records</li>
                    <li>Advanced features (Pharmacy, Lab, Communication)</li>
                    <li>Extended data retention (3-10 years)</li>
                    <li>Priority support</li>
                    <li>Regular updates and new features</li>
                  </ul>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Subscription Plans</h1>
          <p className="text-slate-500 mt-1">Choose the plan that best fits your hospital&apos;s needs</p>
        </div>
        {getPaymentModeBadge()}
      </div>

      {/* Razorpay Setup Instructions */}
      {currentSubscription?.payment_mode === 'razorpay' && !razorpayConfig?.is_configured && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <AlertTriangle className="w-6 h-6 text-amber-600 mt-0.5" />
              <div>
                <h3 className="font-semibold text-amber-800">Razorpay Setup Required</h3>
                <p className="text-amber-700 text-sm mt-1 mb-3">
                  To accept real payments, you need to configure Razorpay API keys in the backend environment.
                </p>
                <div className="bg-white rounded-lg p-4 text-sm font-mono text-slate-700 space-y-1">
                  <p><strong>Steps to configure:</strong></p>
                  <ol className="list-decimal list-inside space-y-1 ml-2">
                    <li>Go to <a href="https://dashboard.razorpay.com" target="_blank" rel="noopener noreferrer" className="text-cyan-600 underline">Razorpay Dashboard</a></li>
                    <li>Navigate to Settings → API Keys</li>
                    <li>Generate new keys (use Test Mode for development)</li>
                    <li>Update backend/.env with your keys:
                      <pre className="bg-slate-100 p-2 rounded mt-1 overflow-x-auto">
{`RAZORPAY_KEY_ID=rzp_test_xxxx
RAZORPAY_KEY_SECRET=xxxx`}
                      </pre>
                    </li>
                    <li>Restart the backend server</li>
                  </ol>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Current Status */}
      {currentSubscription && (
        <Card className="border-l-4 border-l-cyan-500">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-cyan-100 rounded-lg">
                  <CreditCard className="w-6 h-6 text-cyan-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Current Status</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="font-semibold text-slate-900">
                      {isSubscribed 
                        ? currentSubscription.subscription?.plan_name 
                        : currentSubscription.institution_status === 'trial' 
                          ? 'Free Trial'
                          : 'No Subscription'}
                    </span>
                    {getStatusBadge(isSubscribed ? 'active' : currentSubscription.institution_status)}
                  </div>
                </div>
              </div>
              <div className="text-right">
                {isSubscribed ? (
                  <>
                    <p className="text-sm text-slate-500">Valid until</p>
                    <p className="font-medium text-slate-900">
                      {new Date(currentSubscription.subscription?.end_date).toLocaleDateString()}
                    </p>
                    {currentSubscription.subscription?.status === 'active' && (
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="mt-2 text-red-600 hover:text-red-700"
                        onClick={() => setShowCancelModal(true)}
                      >
                        Cancel Subscription
                      </Button>
                    )}
                  </>
                ) : currentSubscription.trial_end_date ? (
                  <>
                    <p className="text-sm text-slate-500">Trial ends</p>
                    <p className="font-medium text-slate-900">
                      {new Date(currentSubscription.trial_end_date).toLocaleDateString()}
                    </p>
                  </>
                ) : null}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Billing Cycle Toggle */}
      <div className="flex justify-center">
        <div className="inline-flex items-center bg-slate-100 rounded-lg p-1">
          <button
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              billingCycle === 'monthly' ? 'bg-white shadow text-slate-900' : 'text-slate-600'
            }`}
            onClick={() => setBillingCycle('monthly')}
            data-testid="billing-monthly"
          >
            Monthly
          </button>
          <button
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              billingCycle === 'yearly' ? 'bg-white shadow text-slate-900' : 'text-slate-600'
            }`}
            onClick={() => setBillingCycle('yearly')}
            data-testid="billing-yearly"
          >
            Yearly <span className="text-emerald-600 ml-1">Save 17%</span>
          </button>
        </div>
      </div>

      {/* Plans Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {plans.map((plan) => {
          const isCurrentPlan = currentPlanId === plan.id;
          const priceFormatted = billingCycle === 'yearly' ? plan.price_yearly_formatted : plan.price_monthly_formatted;
          
          return (
            <Card 
              key={plan.id}
              className={`relative overflow-hidden transition-all ${
                plan.id === 'professional' ? 'ring-2 ring-cyan-500 shadow-lg' : ''
              } ${isCurrentPlan ? 'bg-cyan-50' : ''}`}
              data-testid={`plan-card-${plan.id}`}
            >
              {plan.id === 'professional' && (
                <div className="absolute top-0 right-0 bg-cyan-500 text-white text-xs font-medium px-3 py-1 rounded-bl-lg">
                  Most Popular
                </div>
              )}
              {isCurrentPlan && (
                <div className="absolute top-0 left-0 bg-emerald-500 text-white text-xs font-medium px-3 py-1 rounded-br-lg">
                  Current Plan
                </div>
              )}
              
              <CardHeader className="pb-4">
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center mb-3 ${
                  plan.id === 'starter' ? 'bg-blue-100 text-blue-600' :
                  plan.id === 'professional' ? 'bg-cyan-100 text-cyan-600' :
                  'bg-purple-100 text-purple-600'
                }`}>
                  {getPlanIcon(plan.id)}
                </div>
                <CardTitle>{plan.name}</CardTitle>
                <CardDescription>{plan.description}</CardDescription>
              </CardHeader>
              
              <CardContent className="space-y-6">
                <div>
                  <span className="text-3xl font-bold text-slate-900">{priceFormatted}</span>
                  <span className="text-slate-500">/{billingCycle === 'yearly' ? 'year' : 'month'}</span>
                  {billingCycle === 'yearly' && (
                    <p className="text-sm text-emerald-600 mt-1">
                      Save ₹{plan.yearly_savings?.toLocaleString()}/year
                    </p>
                  )}
                </div>
                
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm">
                    <Users className="w-4 h-4 text-slate-400" />
                    <span>{plan.max_users === -1 ? 'Unlimited' : plan.max_users} users</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <FileText className="w-4 h-4 text-slate-400" />
                    <span>{plan.max_patients_per_month === -1 ? 'Unlimited' : plan.max_patients_per_month?.toLocaleString()} patients/month</span>
                  </div>
                </div>
                
                <div className="border-t pt-4 space-y-2">
                  {plan.features?.map((feature) => (
                    <div key={feature} className="flex items-center gap-2 text-sm text-slate-600">
                      <Check className="w-4 h-4 text-emerald-500" />
                      <span className="capitalize">{feature.replace(/_/g, ' ')}</span>
                    </div>
                  ))}
                </div>
                
                <Button
                  className={`w-full ${
                    isCurrentPlan ? 'bg-emerald-600 hover:bg-emerald-700' :
                    plan.id === 'professional' ? 'bg-cyan-600 hover:bg-cyan-700' :
                    ''
                  }`}
                  variant={isCurrentPlan ? 'default' : plan.id === 'professional' ? 'default' : 'outline'}
                  disabled={isCurrentPlan}
                  onClick={() => handlePlanSelect(plan)}
                  data-testid={`select-plan-${plan.id}`}
                >
                  {isCurrentPlan ? 'Current Plan' : 
                   isSubscribed ? (
                     plan.id === 'enterprise' || (plan.id === 'professional' && currentPlanId === 'starter') ? 'Upgrade' : 'Downgrade'
                   ) : 'Select Plan'}
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Payment History */}
      {history.payments.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Receipt className="w-5 h-5" />
              Payment History
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-4 text-sm font-medium text-slate-500">Date</th>
                    <th className="text-left py-2 px-4 text-sm font-medium text-slate-500">Plan</th>
                    <th className="text-left py-2 px-4 text-sm font-medium text-slate-500">Amount</th>
                    <th className="text-left py-2 px-4 text-sm font-medium text-slate-500">Gateway</th>
                    <th className="text-left py-2 px-4 text-sm font-medium text-slate-500">Status</th>
                    <th className="text-left py-2 px-4 text-sm font-medium text-slate-500">Invoice</th>
                  </tr>
                </thead>
                <tbody>
                  {history.payments.map((payment) => (
                    <tr key={payment.id} className="border-b last:border-0">
                      <td className="py-3 px-4 text-sm">
                        {new Date(payment.created_at).toLocaleDateString()}
                      </td>
                      <td className="py-3 px-4 text-sm capitalize">{payment.plan_id}</td>
                      <td className="py-3 px-4 text-sm font-medium">₹{payment.amount?.toLocaleString()}</td>
                      <td className="py-3 px-4 text-sm capitalize">
                        <span className={`px-2 py-0.5 rounded text-xs ${
                          payment.payment_gateway === 'razorpay' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'
                        }`}>
                          {payment.payment_gateway}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          payment.status === 'success' ? 'bg-emerald-100 text-emerald-700' :
                          payment.status === 'failed' ? 'bg-red-100 text-red-700' :
                          'bg-amber-100 text-amber-700'
                        }`}>
                          {payment.status}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="h-8"
                          onClick={async () => {
                            try {
                              const response = await subscriptionAPI.downloadInvoicePDF(payment.id);
                              const url = window.URL.createObjectURL(new Blob([response.data]));
                              const link = document.createElement('a');
                              link.href = url;
                              link.setAttribute('download', `invoice_INV-${payment.id.slice(0, 8).toUpperCase()}.pdf`);
                              document.body.appendChild(link);
                              link.click();
                              link.remove();
                              window.URL.revokeObjectURL(url);
                              toast.success('Invoice downloaded');
                            } catch (error) {
                              toast.error('Failed to download invoice');
                            }
                          }}
                          data-testid={`download-invoice-${payment.id}`}
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Checkout Modal */}
      <Dialog open={showCheckout} onOpenChange={(open) => { setShowCheckout(open); if (!open) setPlanChangePreview(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {planChangePreview ? (
                planChangePreview.change_type === 'upgrade' ? '⬆️ Upgrade Plan' : '⬇️ Downgrade Plan'
              ) : 'Complete Your Subscription'}
            </DialogTitle>
            <DialogDescription>
              {planChangePreview ? (
                `Change from ${planChangePreview.current_plan.name} to ${planChangePreview.new_plan.name}`
              ) : (
                `Subscribe to ${selectedPlan?.name} plan`
              )}
            </DialogDescription>
          </DialogHeader>
          
          {selectedPlan && (
            <div className="space-y-4 py-4">
              {/* Plan Change Preview */}
              {loadingPreview ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="w-6 h-6 animate-spin text-cyan-600" />
                </div>
              ) : planChangePreview ? (
                <>
                  <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-500">Current Plan</span>
                      <span className="font-medium">{planChangePreview.current_plan.name}</span>
                    </div>
                    <div className="flex items-center justify-center">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        planChangePreview.change_type === 'upgrade' 
                          ? 'bg-emerald-100 text-emerald-700' 
                          : 'bg-amber-100 text-amber-700'
                      }`}>
                        {planChangePreview.change_type === 'upgrade' ? '⬆️ Upgrade' : '⬇️ Downgrade'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-500">New Plan</span>
                      <span className="font-medium">{planChangePreview.new_plan.name}</span>
                    </div>
                  </div>
                  
                  {/* Prorated Credit Info */}
                  {planChangePreview.prorated_credit > 0 && (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                      <div className="text-sm">
                        <p className="font-medium text-emerald-800">Prorated Credit Applied</p>
                        <p className="text-emerald-600">
                          {planChangePreview.remaining_days} days remaining on current plan = {planChangePreview.prorated_credit_formatted} credit
                        </p>
                      </div>
                    </div>
                  )}
                  
                  {/* Amount Summary */}
                  <div className="bg-slate-100 rounded-lg p-4">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-600">New Plan Price</span>
                      <span className="font-medium">{planChangePreview.new_plan.price_formatted}</span>
                    </div>
                    {planChangePreview.prorated_credit > 0 && (
                      <div className="flex justify-between items-center text-emerald-600">
                        <span>Prorated Credit</span>
                        <span>-{planChangePreview.prorated_credit_formatted}</span>
                      </div>
                    )}
                    <div className="flex justify-between items-center border-t mt-2 pt-2">
                      <span className="font-semibold">Amount Due</span>
                      <span className="text-xl font-bold">{planChangePreview.amount_due_formatted}</span>
                    </div>
                  </div>
                  
                  {/* New Limits */}
                  <div className="space-y-2">
                    <Label className="text-slate-600">New Limits:</Label>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-slate-400" />
                        <span>{planChangePreview.new_limits.max_users_display} users</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-slate-400" />
                        <span>{planChangePreview.new_limits.max_patients_display} patients/mo</span>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="bg-slate-50 rounded-lg p-4">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="font-medium">{selectedPlan.name}</p>
                        <p className="text-sm text-slate-500 capitalize">{billingCycle} billing</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xl font-bold">
                          {billingCycle === 'yearly' ? selectedPlan.price_yearly_formatted : selectedPlan.price_monthly_formatted}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Payment Mode Info */}
                  {currentSubscription?.payment_mode === 'razorpay' && razorpayConfig?.is_configured ? (
                    <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <Shield className="w-5 h-5 text-cyan-600 mt-0.5" />
                        <div className="text-sm">
                          <p className="font-medium text-cyan-800">Secure Payment via Razorpay</p>
                          <p className="text-cyan-600">
                            You&apos;ll be redirected to Razorpay&apos;s secure checkout to complete the payment.
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <AlertTriangle className="w-5 h-5 text-purple-600 mt-0.5" />
                        <div className="text-sm">
                          <p className="font-medium text-purple-800">Test Mode Active</p>
                          <p className="text-purple-600">
                            {currentSubscription?.payment_mode === 'razorpay' 
                              ? 'Razorpay keys not configured. Subscription will be activated without real payment.'
                              : 'No actual payment will be processed. Your subscription will be activated immediately.'}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
              
              {/* Features included */}
              <div className="space-y-2">
                <Label className="text-slate-600">What&apos;s included:</Label>
                <div className="grid grid-cols-2 gap-2">
                  {selectedPlan.features?.slice(0, 6).map((feature) => (
                    <div key={feature} className="flex items-center gap-1 text-xs text-slate-600">
                      <Check className="w-3 h-3 text-emerald-500" />
                      <span className="capitalize">{feature.replace(/_/g, ' ')}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCheckout(false); setPlanChangePreview(null); }} disabled={subscribing}>
              Cancel
            </Button>
            <Button 
              onClick={planChangePreview ? handlePlanChange : handleSubscribe}
              disabled={subscribing || loadingPreview}
              className={planChangePreview?.change_type === 'upgrade' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-cyan-600 hover:bg-cyan-700'}
              data-testid="confirm-subscribe"
            >
              {subscribing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : planChangePreview ? (
                planChangePreview.change_type === 'upgrade' ? 'Confirm Upgrade' : 'Confirm Downgrade'
              ) : (
                currentSubscription?.payment_mode === 'razorpay' && razorpayConfig?.is_configured 
                  ? 'Pay with Razorpay'
                  : 'Subscribe Now'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Subscription Modal */}
      <Dialog open={showCancelModal} onOpenChange={setShowCancelModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel Subscription</DialogTitle>
            <DialogDescription>
              Are you sure you want to cancel your subscription? You&apos;ll continue to have access until the end of your current billing period.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="cancelReason">Reason for cancellation (optional)</Label>
            <textarea
              id="cancelReason"
              className="w-full mt-2 p-3 border rounded-md text-sm"
              rows={3}
              placeholder="Tell us why you're cancelling..."
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCancelModal(false)}>
              Keep Subscription
            </Button>
            <Button variant="destructive" onClick={handleCancel} data-testid="confirm-cancel">
              Cancel Subscription
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
